import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { Mail, MessageSquare, ShoppingCart, Send, Eye, ChevronRight, RefreshCw, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const statusBadge = (s) => {
  const map = { sent: { bg: 'rgba(34,197,94,0.1)', c: '#16a34a' }, logged: { bg: 'rgba(59,130,246,0.1)', c: '#2563eb' }, failed: { bg: 'rgba(239,68,68,0.1)', c: '#dc2626' }, skipped: { bg: 'rgba(107,114,128,0.1)', c: '#6b7280' }, invalid_phone: { bg: 'rgba(234,179,8,0.1)', c: '#ca8a04' } };
  const d = map[s] || map.logged;
  return <Badge className="rounded-sm font-sans text-[10px] uppercase" style={{ backgroundColor: d.bg, color: d.c, border: 'none' }}>{s}</Badge>;
};

// ========== EMAIL TAB ==========
function EmailTab() {
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [settings, setSettings] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState('');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [t, l, s] = await Promise.all([api.get('/emails/templates'), api.get('/emails/logs?limit=20'), api.get('/emails/settings')]);
      setTemplates(t.data);
      setLogs(l.data.logs || []);
      setLogTotal(l.data.total || 0);
      setSettings(s.data);
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const previewTemplate = async (id) => {
    try {
      const { data } = await api.get(`/emails/templates/${id}/preview`);
      setPreviewSubject(data.subject);
      setPreviewHtml(data.html);
      setPreviewOpen(true);
    } catch { toast.error('Preview failed'); }
  };

  const sendTest = async (templateId) => {
    if (!testEmail.trim()) { toast.error('Enter a test email address'); return; }
    setSending(templateId);
    try {
      await api.post('/emails/send-test', { template: templateId, to_email: testEmail.trim() });
      toast.success(`Test email queued for ${testEmail}`);
      setTimeout(fetch, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending('');
  };

  if (loading) return <div className="p-8 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</div>;

  return (
    <div className="space-y-8">
      {/* Status */}
      <div className="flex items-center gap-3 p-4 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: settings?.sendgrid_configured ? 'rgba(34,197,94,0.04)' : 'rgba(234,179,8,0.04)' }}>
        {settings?.sendgrid_configured ? <CheckCircle size={18} style={{ color: '#16a34a' }} /> : <Clock size={18} style={{ color: '#ca8a04' }} />}
        <div className="flex-1">
          <p className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>
            {settings?.sendgrid_configured ? 'SendGrid Connected' : 'Log Mode — SendGrid not configured'}
          </p>
          <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>
            {settings?.sendgrid_configured ? 'Emails are being sent via SendGrid' : 'Emails are logged to the database. Add a real SendGrid API key in .env to send.'}
          </p>
        </div>
      </div>

      {/* Test Email Input */}
      <div className="flex items-end gap-3">
        <div className="flex-1 max-w-xs">
          <Label className="font-sans text-xs mb-1 block" style={{ color: 'rgba(26,26,26,0.6)' }}>Test Email Address</Label>
          <Input data-testid="test-email-input" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="admin@store.com" className="font-sans text-sm rounded-none" style={{ borderColor: 'rgba(26,26,26,0.12)' }} />
        </div>
      </div>

      {/* Templates */}
      <div>
        <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Email Templates</h3>
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
          {templates.map(t => (
            <div key={t.id} data-testid={`email-template-${t.id}`} className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-[rgba(26,26,26,0.01)]" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
              <div className="flex-1 min-w-0">
                <p className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>{t.name}</p>
                <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.45)' }}>{t.description}</p>
              </div>
              {t.auto && <Badge className="rounded-sm text-[10px]" style={{ backgroundColor: 'rgba(34,197,94,0.08)', color: '#16a34a', border: 'none' }}>Auto</Badge>}
              <Button variant="outline" size="sm" onClick={() => previewTemplate(t.id)} className="rounded-none font-sans text-xs gap-1 h-8" style={{ borderColor: 'rgba(26,26,26,0.12)' }}>
                <Eye size={12} /> Preview
              </Button>
              <Button variant="outline" size="sm" onClick={() => sendTest(t.id)} disabled={sending === t.id} className="rounded-none font-sans text-xs gap-1 h-8" style={{ borderColor: 'rgba(26,26,26,0.12)' }}>
                {sending === t.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Test
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Logs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-sans text-xs uppercase tracking-wider font-medium" style={{ color: '#C4956A' }}>Email Logs ({logTotal})</h3>
          <Button variant="outline" size="sm" onClick={fetch} className="rounded-none font-sans text-xs gap-1 h-8" style={{ borderColor: 'rgba(26,26,26,0.12)' }}>
            <RefreshCw size={12} /> Refresh
          </Button>
        </div>
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
          <table className="w-full">
            <thead><tr style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              {['Status', 'Template', 'To', 'Subject', 'Date'].map(h => <th key={h} className="text-left p-2.5 font-sans text-[10px] uppercase tracking-wider" style={{ color: 'rgba(26,26,26,0.4)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {logs.length === 0 ? <tr><td colSpan={5} className="p-8 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>No emails sent yet</td></tr> :
                logs.map((l, i) => <tr key={i} className="border-t" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                  <td className="p-2.5">{statusBadge(l.status)}</td>
                  <td className="p-2.5 font-sans text-xs" style={{ color: 'rgba(26,26,26,0.6)' }}>{l.template}</td>
                  <td className="p-2.5 font-sans text-xs" style={{ color: '#1A1A1A' }}>{l.to}</td>
                  <td className="p-2.5 font-sans text-xs max-w-[200px] truncate" style={{ color: 'rgba(26,26,26,0.6)' }}>{l.subject}</td>
                  <td className="p-2.5 font-sans text-[10px]" style={{ color: 'rgba(26,26,26,0.4)' }}>{new Date(l.created_at).toLocaleString()}</td>
                </tr>)
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle className="font-serif">Email Preview: {previewSubject}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-auto border rounded" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
            <iframe title="Email Preview" srcDoc={previewHtml} className="w-full h-[600px] border-0" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== SMS TAB ==========
function SmsTab() {
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [settings, setSettings] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [sending, setSending] = useState('');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [t, l, s] = await Promise.all([api.get('/sms/templates'), api.get('/sms/logs?limit=20'), api.get('/sms/settings')]);
      setTemplates(t.data);
      setLogs(l.data.logs || []);
      setLogTotal(l.data.total || 0);
      setSettings(s.data);
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const sendTest = async (templateId) => {
    if (!testPhone.trim()) { toast.error('Enter a test phone number'); return; }
    setSending(templateId);
    try {
      await api.post('/sms/send-test', { template: templateId, to_phone: testPhone.trim() });
      toast.success(`Test SMS queued for ${testPhone}`);
      setTimeout(fetch, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending('');
  };

  if (loading) return <div className="p-8 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3 p-4 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: settings?.twilio_configured ? 'rgba(34,197,94,0.04)' : 'rgba(234,179,8,0.04)' }}>
        {settings?.twilio_configured ? <CheckCircle size={18} style={{ color: '#16a34a' }} /> : <Clock size={18} style={{ color: '#ca8a04' }} />}
        <div className="flex-1">
          <p className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>{settings?.twilio_configured ? 'Twilio Connected' : 'Log Mode — Twilio not configured'}</p>
          <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>{settings?.twilio_configured ? 'SMS are being sent via Twilio' : 'SMS are logged to the database. Add Twilio credentials in .env to send.'}</p>
        </div>
      </div>

      <div className="flex items-end gap-3">
        <div className="flex-1 max-w-xs">
          <Label className="font-sans text-xs mb-1 block" style={{ color: 'rgba(26,26,26,0.6)' }}>Test Phone (E.164)</Label>
          <Input data-testid="test-phone-input" value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="+15551234567" className="font-sans text-sm rounded-none" style={{ borderColor: 'rgba(26,26,26,0.12)' }} />
        </div>
      </div>

      <div>
        <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>SMS Templates</h3>
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
          {templates.map(t => (
            <div key={t.id} data-testid={`sms-template-${t.id}`} className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-[rgba(26,26,26,0.01)]" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
              <div className="flex-1 min-w-0">
                <p className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>{t.name}</p>
                <p className="font-sans text-xs font-mono" style={{ color: 'rgba(26,26,26,0.4)' }}>{t.sample}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => sendTest(t.id)} disabled={sending === t.id} className="rounded-none font-sans text-xs gap-1 h-8" style={{ borderColor: 'rgba(26,26,26,0.12)' }}>
                {sending === t.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Test
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-sans text-xs uppercase tracking-wider font-medium" style={{ color: '#C4956A' }}>SMS Logs ({logTotal})</h3>
          <Button variant="outline" size="sm" onClick={fetch} className="rounded-none font-sans text-xs gap-1 h-8" style={{ borderColor: 'rgba(26,26,26,0.12)' }}><RefreshCw size={12} /> Refresh</Button>
        </div>
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
          <table className="w-full">
            <thead><tr style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              {['Status', 'Template', 'To', 'Message', 'Date'].map(h => <th key={h} className="text-left p-2.5 font-sans text-[10px] uppercase tracking-wider" style={{ color: 'rgba(26,26,26,0.4)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {logs.length === 0 ? <tr><td colSpan={5} className="p-8 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>No SMS sent yet</td></tr> :
                logs.map((l, i) => <tr key={i} className="border-t" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                  <td className="p-2.5">{statusBadge(l.status)}</td>
                  <td className="p-2.5 font-sans text-xs" style={{ color: 'rgba(26,26,26,0.6)' }}>{l.template}</td>
                  <td className="p-2.5 font-sans text-xs" style={{ color: '#1A1A1A' }}>{l.to}</td>
                  <td className="p-2.5 font-sans text-xs max-w-[240px] truncate" style={{ color: 'rgba(26,26,26,0.6)' }}>{l.body}</td>
                  <td className="p-2.5 font-sans text-[10px]" style={{ color: 'rgba(26,26,26,0.4)' }}>{new Date(l.created_at).toLocaleString()}</td>
                </tr>)
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ========== ABANDONED CART TAB ==========
function AbandonedCartTab() {
  const [stats, setStats] = useState(null);
  const [carts, setCarts] = useState([]);
  const [total, setTotal] = useState(0);
  const [config, setConfig] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [sendingRecovery, setSendingRecovery] = useState('');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, cfg] = await Promise.all([api.get('/abandoned-carts/stats'), api.get('/abandoned-carts?limit=15'), api.get('/abandoned-carts/settings')]);
      setStats(s.data);
      setCarts(c.data.carts || []);
      setTotal(c.data.total || 0);
      setConfig(cfg.data);
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const processNow = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post('/abandoned-carts/process');
      toast.success(`Processed: ${data.processed} emails sent (S1: ${data.stage1}, S2: ${data.stage2}, S3: ${data.stage3})`);
      setTimeout(fetch, 1000);
    } catch { toast.error('Processing failed'); }
    setProcessing(false);
  };

  const sendRecovery = async (token) => {
    setSendingRecovery(token);
    try {
      await api.post(`/abandoned-carts/${token}/send-recovery`);
      toast.success('Recovery email sent');
      setTimeout(fetch, 1000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSendingRecovery('');
  };

  const toggleEnabled = async () => {
    try {
      await api.put('/abandoned-carts/settings', { ...config, enabled: !config?.enabled });
      setConfig(prev => ({ ...prev, enabled: !prev?.enabled }));
      toast.success(config?.enabled ? 'Disabled' : 'Enabled');
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="p-8 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</div>;

  const recoveryStatusColors = { abandoned: { bg: 'rgba(234,179,8,0.1)', c: '#ca8a04' }, stage_1: { bg: 'rgba(59,130,246,0.1)', c: '#2563eb' }, stage_2: { bg: 'rgba(99,102,241,0.1)', c: '#6366f1' }, stage_3: { bg: 'rgba(232,93,58,0.1)', c: '#E85D3A' }, recovered: { bg: 'rgba(34,197,94,0.1)', c: '#16a34a' }, expired: { bg: 'rgba(107,114,128,0.1)', c: '#6b7280' } };

  return (
    <div className="space-y-8">
      {/* Config */}
      <div className="flex items-center justify-between p-4 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
        <div className="flex items-center gap-3">
          <Switch data-testid="abandoned-cart-toggle" checked={config?.enabled !== false} onCheckedChange={toggleEnabled} />
          <div>
            <p className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>Abandoned Cart Recovery</p>
            <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>
              Send recovery emails at {config?.stage1_hours || 1}h, {config?.stage2_hours || 24}h, {config?.stage3_hours || 48}h | Discount: {config?.discount_code || 'COMEBACK10'} ({config?.discount_percent || 10}%)
            </p>
          </div>
        </div>
        <Button data-testid="process-abandoned-btn" onClick={processNow} disabled={processing} className="rounded-none font-sans text-xs gap-1" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
          {processing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Process Now
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Abandoned (30d)', value: stats.total_abandoned },
            { label: 'Recovered', value: stats.total_recovered },
            { label: 'Recovery Rate', value: `${stats.recovery_rate}%` },
            { label: 'Emails Sent', value: stats.emails_sent },
          ].map(({ label, value }) => (
            <div key={label} className="p-4 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
              <p className="font-sans text-xs uppercase tracking-wider mb-1" style={{ color: 'rgba(26,26,26,0.4)' }}>{label}</p>
              <p className="font-sans text-xl font-medium" style={{ color: '#1A1A1A' }}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Carts Table */}
      <div>
        <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Recent Abandoned Carts ({total})</h3>
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
          <table className="w-full">
            <thead><tr style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              {['Customer', 'Items', 'Total', 'Stage', 'Updated', ''].map(h => <th key={h} className="text-left p-2.5 font-sans text-[10px] uppercase tracking-wider" style={{ color: 'rgba(26,26,26,0.4)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {carts.length === 0 ? <tr><td colSpan={6} className="p-8 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>No abandoned carts</td></tr> :
                carts.map(c => {
                  const rs = recoveryStatusColors[c.recovery_status] || recoveryStatusColors.abandoned;
                  return (
                    <tr key={c.session_token} className="border-t hover:bg-[rgba(26,26,26,0.01)]" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                      <td className="p-2.5 font-sans text-xs" style={{ color: '#1A1A1A' }}>{c.customer_email || <span style={{ color: 'rgba(26,26,26,0.3)' }}>No email</span>}</td>
                      <td className="p-2.5 font-sans text-xs" style={{ color: 'rgba(26,26,26,0.6)' }}>{c.item_count} items</td>
                      <td className="p-2.5 font-sans text-xs font-medium" style={{ color: '#1A1A1A' }}>${c.cart_total?.toFixed(2)}</td>
                      <td className="p-2.5"><Badge className="rounded-sm text-[10px] uppercase" style={{ backgroundColor: rs.bg, color: rs.c, border: 'none' }}>{c.recovery_status?.replace(/_/g, ' ')}</Badge></td>
                      <td className="p-2.5 font-sans text-[10px]" style={{ color: 'rgba(26,26,26,0.4)' }}>{new Date(c.updated_at).toLocaleString()}</td>
                      <td className="p-2.5">
                        {c.customer_email && c.recovery_status !== 'recovered' && (
                          <Button variant="outline" size="sm" onClick={() => sendRecovery(c.session_token)} disabled={sendingRecovery === c.session_token} className="rounded-none font-sans text-[10px] h-7 gap-1" style={{ borderColor: 'rgba(26,26,26,0.12)' }}>
                            {sendingRecovery === c.session_token ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />} Recover
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ========== MAIN PAGE ==========
export default function NotificationSettingsPage() {
  return (
    <motion.div data-testid="notification-settings-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="mb-8">
        <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>Notifications</h1>
        <p className="font-sans text-sm mt-1" style={{ color: 'rgba(26,26,26,0.5)' }}>Email, SMS, and abandoned cart recovery</p>
      </div>

      <Tabs defaultValue="email">
        <TabsList className="bg-transparent border-b rounded-none w-full justify-start gap-0 h-auto p-0" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
          <TabsTrigger value="email" data-testid="tab-email" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E85D3A] data-[state=active]:text-[#1A1A1A] font-sans text-sm px-5 py-3 gap-2">
            <Mail size={15} /> Email
          </TabsTrigger>
          <TabsTrigger value="sms" data-testid="tab-sms" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E85D3A] data-[state=active]:text-[#1A1A1A] font-sans text-sm px-5 py-3 gap-2">
            <MessageSquare size={15} /> SMS
          </TabsTrigger>
          <TabsTrigger value="abandoned" data-testid="tab-abandoned" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E85D3A] data-[state=active]:text-[#1A1A1A] font-sans text-sm px-5 py-3 gap-2">
            <ShoppingCart size={15} /> Abandoned Carts
          </TabsTrigger>
        </TabsList>
        <TabsContent value="email" className="pt-6"><EmailTab /></TabsContent>
        <TabsContent value="sms" className="pt-6"><SmsTab /></TabsContent>
        <TabsContent value="abandoned" className="pt-6"><AbandonedCartTab /></TabsContent>
      </Tabs>
    </motion.div>
  );
}
